import { Inbox } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/features/auth';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { StatusBadge } from '@/shared/components/status-badge';
import { formatDateTime } from '@/shared/lib/format';
import { DEFAULT_TIME_ZONE, NON_MARKET_TIME_NOTE } from '@/shared/lib/time-zone';

import { useInbox } from '../api/approvals.queries';
import { DecisionDialog, type Decision } from '../components/decision-dialog';
import { KINDS, kindLabel } from '../lib/approval-kinds';
import type { InboxApproval, InboxItem } from '../types/approval.types';

const STATUSES = ['Pending', 'Approved', 'Rejected', 'Cancelled'] as const;
const ALL_TYPES = 'all';

function age(hours: number) {
  if (hours < 1) return 'under an hour';
  if (hours < 48) return `${Math.floor(hours)} h`;
  return `${Math.floor(hours / 24)} days`;
}

/**
 * The approvals inbox (BR §7.8) — the one place for everything waiting for a
 * second person that the staff member may see: pricing versions, manual FX
 * rates, payment channel fees, refunds and provider balance changes. The
 * backend lists only the types the viewer can read and says whether they may
 * decide each one. Filter by status and type (both in the URL); approve and
 * reject go to each type's own endpoint; "Open" goes to where the item lives.
 */
export function ApprovalsInboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStatus = searchParams.get('status');
  const status = (STATUSES as readonly string[]).includes(requestedStatus ?? '')
    ? (requestedStatus as string)
    : 'Pending';
  const requestedType = searchParams.get('type');
  const type = requestedType && KINDS[requestedType] ? requestedType : undefined;
  const inbox = useInbox({ status, type });
  const { user: me } = useAuth();
  const [deciding, setDeciding] = useState<{
    approval: InboxApproval;
    decision: Decision;
  } | null>(null);

  // One update for both keys: two successive setSearchParams calls overwrite each other.
  const setFilters = (next: { status: string; type: string | undefined }) => {
    const params = new URLSearchParams();
    if (next.status !== 'Pending') params.set('status', next.status);
    if (next.type) params.set('type', next.type);
    setSearchParams(params, { replace: true });
  };

  const items = inbox.data ?? [];
  const pending = status === 'Pending';

  return (
    <PageContainer>
      <PageHeader
        title="Approvals"
        description={`Everything waiting for a second person: pricing versions, manual exchange rates, payment channel fees, refunds and provider balance changes ${NON_MARKET_TIME_NOTE}`}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs value={status} onValueChange={(value) => setFilters({ status: value, type })}>
          <TabsList>
            {STATUSES.map((s) => (
              <TabsTrigger key={s} value={s}>
                {s === 'Pending' ? 'Waiting' : s === 'Cancelled' ? 'Withdrawn' : s}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Select
          value={type ?? ALL_TYPES}
          onValueChange={(value) =>
            setFilters({ status, type: value === ALL_TYPES ? undefined : value })
          }
        >
          <SelectTrigger size="sm" className="h-8 w-48 text-xs" aria-label="Filter by type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TYPES} className="text-xs">
              All types
            </SelectItem>
            {Object.entries(KINDS).map(([value, kind]) => (
              <SelectItem key={value} value={value} className="text-xs">
                {kind.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {inbox.isLoading ? (
        <Skeleton className="h-48" />
      ) : inbox.error ? (
        <ErrorState error={inbox.error} onRetry={() => void inbox.refetch()} />
      ) : items.length === 0 ? (
        type ? (
          <EmptyState
            icon={Inbox}
            title={`No ${kindLabel(type).toLowerCase()} requests here`}
            description="Show every type to see the other requests."
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ status, type: undefined })}
              >
                Show all types
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={Inbox}
            title={pending ? 'Nothing waiting' : 'Nothing here'}
            description="Requests you may see appear here."
          />
        )
      ) : (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Request</TableHead>
                <TableHead>Asked by</TableHead>
                <TableHead>Reason</TableHead>
                {pending ? (
                  <TableHead>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                ) : (
                  <TableHead>Decision</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <InboxRow
                  key={`${item.approval.type}-${item.approval.id}`}
                  item={item}
                  isMine={me?.id === item.approval.requestedByUserId}
                  onDecide={(decision) => setDeciding({ approval: item.approval, decision })}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {deciding && (
        <DecisionDialog
          approval={deciding.approval}
          decision={deciding.decision}
          onClose={() => setDeciding(null)}
        />
      )}
    </PageContainer>
  );
}

function InboxRow({
  item,
  isMine,
  onDecide,
}: {
  item: InboxItem;
  isMine: boolean;
  onDecide: (decision: Decision) => void;
}) {
  const { approval } = item;
  const kind = KINDS[approval.type];
  const pending = approval.status === 'Pending';

  return (
    <TableRow>
      <TableCell>
        <span className="text-muted-foreground block text-[11px] font-medium uppercase">
          {kindLabel(approval.type)}
        </span>
        <span className="font-medium">{approval.summary}</span>
        {kind && (
          <Link to={kind.link(approval)} className="text-primary ms-2 text-xs hover:underline">
            {kind.linkLabel ?? 'Open'}
          </Link>
        )}
      </TableCell>
      <TableCell>
        {isMine ? 'You' : (approval.requestedByName ?? '—')}
        <span className="text-muted-foreground block text-xs">
          {formatDateTime(approval.requestedAtUtc, DEFAULT_TIME_ZONE)}
          {pending && <> · {age(item.ageHours)}</>}
        </span>
        {item.isAgeing && (
          <StatusBadge variant="warning" className="mt-1">
            Waiting too long
          </StatusBadge>
        )}
      </TableCell>
      <TableCell className="max-w-64">{approval.requestReason ?? '—'}</TableCell>
      {pending ? (
        <TableCell className="text-end">
          <span className="flex justify-end gap-1.5">
            {/* Your own request: withdraw it, unless your role may also approve it. */}
            {(item.canDecide || !isMine) && (
              <>
                <Button
                  size="sm"
                  className="h-7"
                  disabled={!item.canDecide}
                  title={item.cannotDecideReason ?? undefined}
                  aria-label={`Approve ${approval.summary}`}
                  onClick={() => onDecide('approve')}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  disabled={!item.canDecide}
                  title={item.cannotDecideReason ?? undefined}
                  aria-label={`Reject ${approval.summary}`}
                  onClick={() => onDecide('reject')}
                >
                  Reject
                </Button>
              </>
            )}
            {isMine && (
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                aria-label={`Withdraw ${approval.summary}`}
                onClick={() => onDecide('cancel')}
              >
                Withdraw
              </Button>
            )}
          </span>
          {!item.canDecide && !isMine && item.cannotDecideReason && (
            <span className="text-muted-foreground mt-1 block text-[11px]">
              {item.cannotDecideReason}
            </span>
          )}
        </TableCell>
      ) : (
        <TableCell>
          <StatusBadge
            variant={
              approval.status === 'Approved'
                ? 'success'
                : approval.status === 'Rejected'
                  ? 'danger'
                  : 'neutral'
            }
          >
            {approval.status === 'Cancelled' ? 'Withdrawn' : approval.status}
          </StatusBadge>
          {approval.decidedByName && (
            <span className="text-muted-foreground block text-xs">
              {approval.decidedByName}
              {approval.decidedAtUtc
                ? ` · ${formatDateTime(approval.decidedAtUtc, DEFAULT_TIME_ZONE)}`
                : ''}
              {approval.decisionReason ? ` — ${approval.decisionReason}` : ''}
            </span>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
